import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { Plus, Loader2, Users, Send, Bell, CheckCircle2, Link2 } from "lucide-react";
import {
  HeroCard, StatCard, SectionHeader, InsightCard
} from "@/components/marketing/common";
import { MARKETING_COLORS } from "@/config/marketingColors";

function timeAgo(iso) {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 60) return `${m || 1}min atrás`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h atrás`;
  return `${Math.floor(h / 24)}d atrás`;
}

const NotificacionesPushTab = () => {
  const { authAxios } = useAuth();
  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;
  const [campaigns, setCampaigns] = useState([]);
  const [subscriberCount, setSubscriberCount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", url: "" });

  const load = useCallback(async () => {
    try {
      const [campRes, subRes] = await Promise.all([
        authAxiosRef.current.get("/push/campaigns"),
        authAxiosRef.current.get("/push/subscribers/count"),
      ]);
      setCampaigns(campRes.data || []);
      setSubscriberCount(subRes.data?.count ?? 0);
    } catch { /* ignore */ }
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSend = async () => {
    if (!form.title.trim() || !form.body.trim()) {
      toast.error("Título e mensagem são obrigatórios");
      return;
    }
    setSending(true);
    try {
      const res = await authAxios.post("/push/campaigns", form);
      toast.success(`Enviado para ${res.data.sent} cliente${res.data.sent !== 1 ? "s" : ""}!`);
      setShowDialog(false);
      setForm({ title: "", body: "", url: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao enviar notificação");
    }
    setSending(false);
  };

  const totalSent = campaigns.reduce((sum, c) => sum + (c.sent ?? 0), 0);

  return (
    <div className="space-y-6">
      {/* Hero Section */}
      <HeroCard
        title="Push Notifications"
        metric={subscriberCount ?? 0}
        subMetrics={[
          {
            label: "Assinantes",
            value: `${subscriberCount ?? 0} cliente${subscriberCount !== 1 ? "s" : ""}`,
          },
          {
            label: "Total Enviado",
            value: totalSent.toString(),
          },
        ]}
        color={MARKETING_COLORS.Push}
      />

      {/* Stats Cards */}
      <div className="grid lg:grid-cols-3 md:grid-cols-2 sm:grid-cols-1 gap-4">
        <StatCard
          label="Campanhas Criadas"
          value={campaigns.length.toString()}
          icon={Bell}
          trend="neutral"
          change={0}
        />
        <StatCard
          label="Total Notificações Enviadas"
          value={totalSent.toLocaleString()}
          icon={Send}
          trend="neutral"
          change={0}
        />
        <StatCard
          label="Taxa de Entrega"
          value={subscriberCount > 0 ? `${((totalSent / (subscriberCount * campaigns.length)) * 100).toFixed(1)}%` : "—"}
          icon={CheckCircle2}
          trend="neutral"
          change={0}
        />
      </div>

      {/* Subscriber info banner */}
      {!loading && subscriberCount === 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 flex items-start gap-3">
          <Bell className="w-5 h-5 mt-0.5 shrink-0 text-amber-600" />
          <div>
            <p className="font-semibold">Ainda sem assinantes</p>
            <p className="text-xs mt-1">Quando clientes abrirem o menu QR e aceitarem notificações push, aparecerão aqui automaticamente.</p>
          </div>
        </div>
      )}

      {/* Send Button */}
      <div className="flex justify-end">
        <Button size="lg" className="gap-2" onClick={() => setShowDialog(true)}>
          <Plus className="w-4 h-4" />
          Enviar Notificação
        </Button>
      </div>

      {/* Campaign list */}
      <SectionHeader
        title="Histórico de Campanhas"
        subtitle={`${campaigns.length} notificação(ões) enviada(s)`}
      />

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : campaigns.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Bell className="w-12 h-12 mx-auto text-muted-foreground mb-4 opacity-50" />
            <p className="text-muted-foreground">Nenhuma notificação enviada ainda. Crie a primeira acima!</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {campaigns.map(c => (
            <Card key={c.id} className="overflow-hidden hover:shadow-md transition">
              <CardContent className="flex items-center justify-between p-5 gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                    <p className="font-semibold text-sm line-clamp-1">{c.title}</p>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{c.body}</p>
                  <p className="text-xs text-muted-foreground mt-2">{timeAgo(c.created_at)}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-3xl font-bold text-primary">{c.sent ?? 0}</p>
                  <p className="text-xs text-muted-foreground">enviado{c.sent !== 1 ? "s" : ""}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Benefits Insights */}
      <SectionHeader
        title="Estratégia de Notificações"
        subtitle="Dicas para melhor engajamento"
      />

      <div className="grid md:grid-cols-3 gap-4">
        <InsightCard
          type="insight"
          title="Frequência Ideal"
          message="Envie 1-2 notificações por semana. Muitas notificações causam desinscrição em massa."
        />
        <InsightCard
          type="success"
          title="Melhor Horário"
          message="Envie entre 11h-13h (almoço) ou 18h-20h (jantar) para máximo engajamento em restaurantes."
        />
        <InsightCard
          type="alert"
          title="Personalize o Conteúdo"
          message="Use emojis, crie senso de urgência (promoções limitadas) e sempre mencione benefício claro."
        />
      </div>

      {/* New campaign dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-4 h-4" /> Nova Notificação Push
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {subscriberCount !== null && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted rounded-md px-3 py-2">
                <Users className="w-4 h-4" />
                Será enviado para <span className="font-bold text-foreground">{subscriberCount}</span> assinante{subscriberCount !== 1 ? "s" : ""}
              </div>
            )}
            <div className="space-y-1">
              <Label>Título *</Label>
              <Input
                placeholder="ex: Promoção de fim de semana! 🍣"
                value={form.title}
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                maxLength={60}
              />
              <p className="text-xs text-muted-foreground text-right">{form.title.length}/60</p>
            </div>
            <div className="space-y-1">
              <Label>Mensagem *</Label>
              <Textarea
                placeholder="ex: Hoje 20% de desconto em todos os rolls. Válido até às 22h!"
                value={form.body}
                onChange={e => setForm(f => ({ ...f, body: e.target.value }))}
                rows={3}
                maxLength={150}
              />
              <p className="text-xs text-muted-foreground text-right">{form.body.length}/150</p>
            </div>
            <div className="space-y-1">
              <Label className="flex items-center gap-1">
                <Link2 className="w-3 h-3" /> Link ao clicar (opcional)
              </Label>
              <Input
                placeholder="Deixar em branco → abre o menu"
                value={form.url}
                onChange={e => setForm(f => ({ ...f, url: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)} disabled={sending}>Cancelar</Button>
            <Button onClick={handleSend} disabled={sending || !form.title.trim() || !form.body.trim()} className="gap-2">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {sending ? "Enviando..." : "Enviar agora"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export { NotificacionesPushTab };
